import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import { toastr } from 'react-redux-toastr';

import { saveFormReviewState, getStorageSetting } from 'actions';

import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';

import AutoCompleteInput from 'components/Common/Inputs/AutoCompleteInput';
import FormWorkflowStages from 'common/Workflows/FormWorkflowStages';

class FormReviewWorkflowSection extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    permissions: PropTypes.arrayOf(PropTypes.string),
    formId: PropTypes.number,
    formReviewState: PropTypes.shape(),
    workflowState: PropTypes.shape(),
    validReviewer: PropTypes.bool,
    isLocked: PropTypes.bool,
    isSavingFormReviewState: PropTypes.bool,
    hasValidationResult: PropTypes.bool,
    isPiiProtectionEnabled: PropTypes.bool,
    isFormDirty: PropTypes.bool
  };

  static defaultProps = {
    permissions: null,
    formId: null,
    formReviewState: null,
    workflowState: null,
    validReviewer: true,
    isLocked: false,
    isSavingFormReviewState: false,
    hasValidationResult: false,
    isPiiProtectionEnabled: false,
    isFormDirty: false
  };

  constructor(props) {
    super(props);
    this.state = {
      assignToUser: null,
      isKycReviewed: props.formReviewState ? props.formReviewState.isKycReviewed : false,
    };
  }

  onAssignToUserSelectedItem = (lookupValue) => {
    this.setState({ assignToUser: lookupValue });
  };

  onAssignToUserChange = (e, value) => {
    const { assignToUser } = this.state;

    if (value === '') {
      this.setState({ assignToUser: null });
    }

    if (assignToUser && assignToUser.value !== value) {
      this.setState({ assignToUser: null });
    }
  };

  onKycChecked = (value) => {
    this.setState({ isKycReviewed: value });
  };

  onFormSubmit = (actionId) => {
    const { dispatch, formId, formReviewState, hasValidationResult, isFormDirty } = this.props;
    const { assignToUser, isKycReviewed } = this.state;

    if (!hasValidationResult) {
      toastr.warning('', 'Please check the form for validation results before submitting.');
      return;
    }

    // eslint-disable-next-line no-alert
    if (isFormDirty && !window.confirm('Are you sure you want to leave this page?')) {
      return;
    }

    dispatch(saveFormReviewState(formId, {
      actionId,
      draftComments: formReviewState.draftComments,
      assignToUser,
      isKycReviewed
    }));

    this.setState({ assignToUser: '' });
  };

  canComment() {
    const { permissions, formReviewState, workflowState, validReviewer, isLocked } = this.props;
    if (!validReviewer || isLocked) {
      return false;
    }

    const isUserFormOwner = workflowState != null && workflowState.assignedUser === getStorageSetting('username');
    const hasReviewerPermission = AccessControl.hasPermission(permissions, [ActivityPerms.fwfInitialReviewer, ActivityPerms.fwfSecondaryReviewer, ActivityPerms.fwfExpertReviewer, ActivityPerms.fwfSupplReviewer]);

    if (formReviewState != null && formReviewState.workflowStage != null) {
      if (formReviewState.workflowStage === FormWorkflowStages.BatchStaging) {
        // WF is Batch Staging
        return false;
      }
      if (formReviewState.workflowStage === FormWorkflowStages.Complete) {
        // WF is complete
        return AccessControl.hasPermission(permissions, [ActivityPerms.fwfExpertReviewer, ActivityPerms.fwfSupplReviewer]);
      }
    }
    return isUserFormOwner && hasReviewerPermission;
  }

  render() {
    const {
      formReviewState,
      isSavingFormReviewState,
      isPiiProtectionEnabled,
      workflowState
    } = this.props;
    const { assignToUser, isKycReviewed } = this.state;
    const isCompleted = formReviewState.workflowStage === FormWorkflowStages.Complete;

    return (
      <div>
        <div className="form-review-actions-content">
          <form
            id="formReviewForm"
            className="form-horizontal side-bar-form"
          >
            {!isPiiProtectionEnabled && <div className="form-group">
              <div className="col-4 col-sm-12">
                <label htmlFor="kyc-reviewed" className="form-label">KYC Review</label>
              </div>
              <div>
                <label className="form-checkbox">
                  Check this box if a KYC review has taken place as part of the validation process
                  <input
                    type="checkbox"
                    id="KYC"
                    name="isKYC"
                    value="true"
                    disabled={ isCompleted || !this.canComment() }
                    checked={ isKycReviewed }
                    onChange={ e => this.onKycChecked(e.target.checked) }
                  />
                  <i className="form-icon" />
                </label>
              </div>
            </div>}
            { this.canComment() && <div className="assign-to-user form-group">
              <div className="col-4 col-sm-12">
                <label htmlFor="assigned-user" className="form-label">Assign To User</label>
              </div>
              <div className="col-8 col-sm-12">
                <AutoCompleteInput
                  id="assigned-user"
                  name="assignToUser"
                  category="user"
                  placeholder="Enter user name..."
                  disabled={ isCompleted }
                  value={ assignToUser || '' }
                  onSelectedItem={ lookupValue => this.onAssignToUserSelectedItem(lookupValue) }
                  onChange={ (e, value) => this.onAssignToUserChange(e, value) }
                />
              </div>
            </div> }
            { this.canComment() && <div className="review-actions form-group">
              { formReviewState != null && formReviewState.availableActions != null && <div className="workflow-actions col-12">
                { Object.entries(formReviewState.availableActions) != null && Object.entries(formReviewState.availableActions)
                  .filter(([key, value]) => value != null && value !== 'Submit').map(([key, value]) => key != null && <div key={ key } className="col-xl-12 col-6">
                    <button
                      value={ value }
                      className="btn text-ellipsis"
                      title={ value }
                      type="button"
                      disabled={ isSavingFormReviewState }
                      onClick={ () => this.onFormSubmit(key) }
                    >
                      { value }
                    </button>
                  </div>) }
                {isPiiProtectionEnabled && workflowState.stage >= 100 && <div className="col-6">
                  <label className="pii-review-label form-checkbox">
                    Check this box to confirm a PII review has taken place
                    <input
                      type="checkbox"
                      id="KYC"
                      name="isKYC"
                      value="true"
                      disabled
                      checked
                    />
                    <i className="form-icon" />
                  </label>
                </div>}
              </div> }
              <div className="submit-actions col-12">
                <div className="col-6">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={ isSavingFormReviewState }
                    onClick={ () => this.onFormSubmit(null) }
                  >
                    Save { assignToUser && ' and Assign' }
                  </button>
                </div>
                <div className="col-6">
                  { formReviewState && formReviewState.availableActions && Object.entries(formReviewState.availableActions) != null && Object.entries(formReviewState.availableActions)
                    .filter(([key, value]) => value != null && value === 'Submit').map(([key, value]) => key != null && <button
                      key={ key }
                      value={ value }
                      className="btn btn-primary review-submit"
                      type="button"
                      disabled={ isSavingFormReviewState || (isPiiProtectionEnabled && !isKycReviewed) }
                      onClick={ () => this.onFormSubmit(key) }
                    >
                      { value }
                    </button>) }
                  {isPiiProtectionEnabled && workflowState.stage < 100 && <div className="pii-review-label">
                    <label className="pii-review-label form-checkbox">
                      Check this box to confirm a PII review has taken place
                      <input
                        type="checkbox"
                        id="KYC"
                        name="isKYC"
                        value="false"
                        disabled={ isCompleted || !this.canComment() || (workflowState && workflowState.stage > 5) }
                        checked={ isKycReviewed || (workflowState && workflowState.stage > 5) }
                        onChange={ e => this.onKycChecked(e.target.checked) }
                      />
                      <i className="form-icon" />
                    </label>
                    </div>}
                </div>
              </div>
            </div> }
          </form>
        </div>
      </div>
    );
  }
}

const formReviewForm = reduxForm({
  form: 'formReviewForm',
})(FormReviewWorkflowSection);

function mapStateToProps(state) {
  const {
    auth: {
      permissions,
      settings: {
        selectedClient
      }
    },
    forms: {
      isSavingFormReviewState,
      formReviewState,
      workflowState
    }
  } = state;

  return {
    permissions,
    isPiiProtectionEnabled: selectedClient.isPiiProtectionEnabled,
    isSavingFormReviewState,
    formReviewState,
    workflowState
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export const FormReviewWorkflow = connect(
  mapStateToProps,
  mapDispatchToProps
)(formReviewForm);

export default FormReviewWorkflow;
